import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { AutoAwesomeOutlined, DirectionsCarOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Badge, Button, Callout, PageHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useLazyGetPlatformCatalogQuery } from "@api/catalogs";
import { useLazyPalencaGigWidgetUrlQuery } from "@api/sat_apis_new/satApsi";
import { PlatformCatalogItem } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

type Plataforma = "didi" | "uber";

const PLATAFORMAS: Record<
  Plataforma,
  { label: string; code: string; opcional: boolean; descripcion: string; notaConectada: string }
> = {
  didi: {
    label: "Didi",
    // `code` de la plataforma en `platform_config`.
    code: "PLTDID",
    opcional: false,
    descripcion: "Iniciarás sesión en Didi de forma segura; nunca vemos tu contraseña.",
    notaConectada: "Ya leímos tu actividad para armar tu oferta.",
  },
  uber: {
    label: "Uber",
    code: "PLTUBR",
    opcional: true,
    descripcion: "Si también manejas en Uber, sumamos tu actividad en las dos apps.",
    notaConectada: "Sumaremos tu actividad en Didi y Uber.",
  },
};

const PLATAFORMAS_ORDEN: Plataforma[] = ["didi", "uber"];

const buscarEnCatalogo = (catalogo: PlatformCatalogItem[], plataforma: Plataforma) =>
  catalogo.find((item) => item.code === PLATAFORMAS[plataforma].code);

/**
 * Cuentas ya conectadas de la solicitud. Al conectar se sale a Palenca y se regresa con `?connected=true`
 * (la página se recarga), así que se guardan en `sessionStorage`, igual que cuál se estaba conectando.
 */
const conectadasKey = (applicationId: string) => `perfil-conductor-conectadas-${applicationId}`;
const conectandoKey = (applicationId: string) => `perfil-conductor-conectando-${applicationId}`;
const leerConectadas = (applicationId: string): Record<Plataforma, boolean> => {
  try {
    return { didi: false, uber: false, ...JSON.parse(sessionStorage.getItem(conectadasKey(applicationId)) ?? "{}") };
  } catch {
    return { didi: false, uber: false };
  }
};

interface CardPlataformaProps {
  plataforma: Plataforma;
  conectada: boolean;
  conectando: boolean;
  disabled: boolean;
  onConectar: () => void;
}

/** Tarjeta de una cuenta de plataforma: por conectar (con su botón) o ya verificada. */
const CardPlataforma = ({ plataforma, conectada, conectando, disabled, onConectar }: CardPlataformaProps) => {
  const { label, opcional, descripcion, notaConectada } = PLATAFORMAS[plataforma];

  return (
    <Paper variant="lendia_paper" sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          bgcolor: "primary.dark",
          color: "primary.contrastText",
          flexShrink: 0,
        }}
      >
        <DirectionsCarOutlined />
      </Box>
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "primary.dark" }}>
            {conectada ? `Cuenta ${label} verificada` : `Conecta tu cuenta de ${label}`}
          </Typography>
          {!conectada && <Badge>{opcional ? "Opcional" : "Requerido"}</Badge>}
        </Box>
        <Typography variant="body2" sx={{ color: "tokens.textTertiary" }}>
          {conectada ? notaConectada : descripcion}
        </Typography>
      </Box>
      {conectada ? (
        <Badge tone="ok">Conectado</Badge>
      ) : (
        <Button
          tone={opcional ? "surface" : "navy"}
          withArrow={!conectando}
          onClick={onConectar}
          disabled={disabled}
          sx={{ minWidth: 180 }}
          data-testid={`perfil-conductor-conectar-${plataforma}-button`}
        >
          {conectando ? "Conectando…" : `Conectar con ${label}`}
        </Button>
      )}
    </Paper>
  );
};

/**
 * "Perfil de conductor" de WuWei (status CSPCO, `WW_Plataforma` del prototipo): Didi y Uber en la misma pantalla.
 * Didi es requerida para continuar; Uber es opcional y suma actividad. No se muestran estadísticas del conductor.
 * La conexión es la del legacy `tofu/pm/InformacionPlataforma`: catálogo de plataformas (`platform_config`) y el
 * widget de Palenca (`palenca-gig-widget-url`), que se abre en la misma pestaña y regresa con `?connected=true`.
 */
export const PerfilConductorWuWei = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.perfil_conductor.code);

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getPlatformCatalog] = useLazyGetPlatformCatalogQuery();
  const [getWidgetUrl] = useLazyPalencaGigWidgetUrlQuery();

  const applicationId = String(application.id);
  const [catalogo, setCatalogo] = useState<PlatformCatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [conectadas, setConectadas] = useState<Record<Plataforma, boolean>>(() => leerConectadas(applicationId));
  const [conectando, setConectando] = useState<Plataforma | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const marcarConectada = (plataforma: Plataforma) => {
    setConectadas((actual) => {
      const siguiente = { ...actual, [plataforma]: true };
      sessionStorage.setItem(conectadasKey(applicationId), JSON.stringify(siguiente));
      return siguiente;
    });
  };

  // Al regresar de Palenca (`?connected=true`) se marca la plataforma que se estaba conectando; luego, el catálogo.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pendiente = sessionStorage.getItem(conectandoKey(applicationId)) as Plataforma | null;
    if (params.get("connected") === "true" && pendiente && PLATAFORMAS_ORDEN.includes(pendiente)) {
      marcarConectada(pendiente);
    }
    sessionStorage.removeItem(conectandoKey(applicationId));

    const fetchCatalogo = async () => {
      try {
        setIsLoading(true);
        setCatalogo(await getPlatformCatalog({ taxcode: application.type }).unwrap());
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "PerfilConductorWuWei-catalogo" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };
    fetchCatalogo();
    // Solo al entrar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Como el legacy: pide la URL del widget de Palenca y la abre en la misma pestaña. Sin URL (plataforma sin
   * configurar) se reporta y la cuenta se da por conectada para no bloquear el flujo.
   */
  const conectar = async (plataforma: Plataforma) => {
    const item = buscarEnCatalogo(catalogo, plataforma);
    try {
      setConectando(plataforma);
      if (!item) throw new Error(`No se encontró ${PLATAFORMAS[plataforma].label} en el catálogo de plataformas`);

      const response = item.widget_id
        ? await getWidgetUrl({ loan_application_id: applicationId, plattform: item.widget_id }).unwrap()
        : null;
      if (response?.widget_url) {
        sessionStorage.setItem(conectandoKey(applicationId), plataforma);
        window.open(response.widget_url, "_self");
        return;
      }

      Sentry.captureMessage("palenca-gig-widget-url sin widget_url", {
        level: "warning",
        extra: {
          component: "PerfilConductorWuWei-conectar",
          loan_application_id: applicationId,
          platform_code: item.code,
          widget_id: item.widget_id,
        },
      });
      marcarConectada(plataforma);
      setConectando(null);
    } catch (error) {
      setConectando(null);
      Sentry.captureException(error, { extra: { component: "PerfilConductorWuWei-conectar", plataforma } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const handleContinuar = async () => {
    try {
      setIsSubmitting(true);
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "PerfilConductorWuWei-continuar" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Perfil de conductor"
        title="Verifica tu perfil de conductor"
        subtitle="Conecta tu cuenta de Didi para comprobar tu actividad. Usamos tu historial como conductor para construir una mejor oferta, más allá de tu buró. Solo leemos tu estatus, calificación y actividad."
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
        {PLATAFORMAS_ORDEN.map((plataforma) =>
          isLoading ? (
            <Skeleton key={plataforma} variant="rounded" height={82} />
          ) : (
            <CardPlataforma
              key={plataforma}
              plataforma={plataforma}
              conectada={conectadas[plataforma]}
              conectando={conectando === plataforma}
              disabled={Boolean(conectando) || isSubmitting}
              onConectar={() => conectar(plataforma)}
            />
          ),
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Callout tone="info" icon={<AutoAwesomeOutlined sx={{ fontSize: 16 }} />}>
          Un buen historial en plataforma <strong>mejora tu tasa y tu monto</strong>.
          {!conectadas.uber && " Si solo manejas en Didi, continúa sin conectar Uber."}
        </Callout>
      </Box>

      <StepActions
        onBack={() => {
          if (previousStatus) navigate(previousStatus.url);
        }}
        onNext={handleContinuar}
        backLabel="Atrás"
        // TODO: volver a exigir Didi conectada (`!conectadas.didi`) cuando el back regrese de Palenca a esta pantalla;
        // mientras, se deja continuar sin conectar.
        nextDisabled={isSubmitting || Boolean(conectando)}
        backTestId="perfil-conductor-back-button"
        nextTestId="perfil-conductor-continue-button"
      />
    </Box>
  );
};
